import { useMemo, useState } from 'react';
import { clsx } from 'clsx';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/shared/ui';
import { Skeleton } from '@/shared/ui/Skeleton';
import { useWorkQueue, useWorkSummary, useMovers } from '../hooks/usePortfolio';
import type { BallWith, WorkItem } from '../api/portfolioApi';
import type { NotificationCategory } from '../api/commandCenterApi';
import { WorkCard } from '../components/portfolio/WorkCard';
import {
  BALL_META,
  CATEGORY_LABEL,
  CATEGORY_ORDER,
  formatAge,
  formatMoney,
} from '../components/portfolio/constants';
import styles from './AgencyTodayPage.module.css';

const LANES: BallWith[] = ['us', 'client', 'auto'];

/**
 * Today — the agency's work queue.
 *
 * One list of work across every practice, ordered by when each item comes due.
 * The old Command Center listed practices and buried their work in a drawer
 * behind Action / Review / FYI tabs, which answered "who has the most unread
 * items" rather than "what do I do next".
 */
export function AgencyTodayPage({ embedded = false }: { embedded?: boolean } = {}) {
  const navigate = useNavigate();
  const [lane, setLane] = useState<BallWith>('us');
  const [category, setCategory] = useState<NotificationCategory | null>(null);

  const { data: summaryData, isLoading: summaryLoading } = useWorkSummary();
  const { data: items, isLoading: itemsLoading } = useWorkQueue({
    ball: lane,
    category: category ?? undefined,
  });

  const summary = summaryData?.summary;
  const closed = summaryData?.closed ?? [];

  const laneCounts: Record<BallWith, number> = {
    us: summary?.needsUs ?? 0,
    client: summary?.waitingOnClient ?? 0,
    auto: summary?.automated ?? 0,
  };

  const openPractice = (practiceId: string) => navigate(`/agency/practices/${practiceId}`);

  const buckets = useMemo(() => groupByDue(items ?? [], lane), [items, lane]);

  const today = new Date().toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  return (
    <div className={styles.page}>
      {/* As the Queue view of the Practices page, the page header is the Practices header. */}
      {!embedded && (
        <PageHeader
          title="Today"
          subtitle={
            summary
              ? `${summary.needsUs} items need you · ${summary.waitingOnClient} waiting on a client · ${today}`
              : today
          }
        />
      )}

      <div className={styles.statRow}>
        {summaryLoading || !summary ? (
          Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} height={82} borderRadius="var(--card-radius)" />
          ))
        ) : (
          <>
            <WeekStat
              label="Needs you"
              value={summary.needsUs}
              tone={summary.needsUs > 0 ? 'warning' : 'success'}
              sub="by due time"
            />
            <WeekStat
              label="Waiting on client"
              value={summary.waitingOnClient}
              tone="neutral"
              sub={`${summary.blockedOverThreeDays} blocked over 3 days`}
            />
            <WeekStat
              label="Closed this week"
              value={summary.closedThisWeek}
              tone="success"
              sub="resolved or dismissed"
            />
            <WeekStat
              label="Spend at risk"
              value={formatMoney(summary.spendAtRisk)}
              tone="error"
              sub="30d spend over own 60-day baseline"
            />
          </>
        )}
      </div>

      <div className={styles.layout}>
        <div className={styles.main}>
          <div className={styles.filters}>
            {LANES.map((key) => (
              <button
                key={key}
                type="button"
                className={clsx(styles.lane, lane === key && styles.laneActive, styles[key])}
                onClick={() => setLane(key)}
                aria-pressed={lane === key}
              >
                {BALL_META[key].lane}
                <span className={styles.laneCount}>{laneCounts[key]}</span>
              </button>
            ))}

            <span className={styles.filterDivider} aria-hidden />
            <span className={styles.filterLabel}>Module</span>
            {CATEGORY_ORDER.map((key) => {
              const count = summary?.byCategory[key] ?? 0;
              const active = category === key;
              return (
                <button
                  key={key}
                  type="button"
                  className={clsx(styles.moduleChip, active && styles.moduleChipActive)}
                  onClick={() => setCategory(active ? null : key)}
                  disabled={count === 0 && !active}
                  aria-pressed={active}
                >
                  {CATEGORY_LABEL[key]}
                  {count > 0 && <span className={styles.moduleCount}>{count}</span>}
                </button>
              );
            })}
          </div>

          {itemsLoading && (
            <div className={styles.bucketList}>
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} height={104} borderRadius="var(--card-radius)" />
              ))}
            </div>
          )}

          {!itemsLoading &&
            buckets.map((bucket) =>
              bucket.items.length === 0 ? null : (
                <section key={bucket.key} className={styles.bucket}>
                  <div className={styles.bucketHeader}>
                    <h2
                      className={clsx(
                        styles.bucketTitle,
                        bucket.key === 'overdue' && styles.bucketTitleOverdue,
                      )}
                    >
                      {bucket.label}
                    </h2>
                    <span className={styles.bucketCount}>{bucket.items.length}</span>
                    {bucket.hint && <span className={styles.bucketHint}>{bucket.hint}</span>}
                  </div>
                  <div className={styles.bucketList}>
                    {bucket.items.map((item) => (
                      <WorkCard key={item.id} item={item} onOpenPractice={openPractice} />
                    ))}
                  </div>
                </section>
              ),
            )}

          {!itemsLoading && (items?.length ?? 0) === 0 && (
            <div className={styles.empty}>
              <p className={styles.emptyTitle}>Nothing left in this lane</p>
              <p className={styles.emptyBody}>
                {lane === 'us'
                  ? `${laneCounts.client} item${laneCounts.client === 1 ? ' is' : 's are'} waiting on a client. Nudging the oldest is usually the next move.`
                  : 'Switch lanes to see what else is open.'}
              </p>
            </div>
          )}
        </div>

        <aside className={styles.rail}>
          <RailPanel title="Where the ball is">
            {LANES.map((key) => {
              const total = laneCounts.us + laneCounts.client + laneCounts.auto || 1;
              return (
                <div key={key} className={styles.ballRow}>
                  <div className={styles.ballLabel}>
                    <span>{BALL_META[key].label}</span>
                    <span className={styles.ballCount}>{laneCounts[key]}</span>
                  </div>
                  <div className={styles.ballTrack}>
                    <div
                      className={clsx(styles.ballFill, styles[key])}
                      style={{ width: `${(laneCounts[key] / total) * 100}%` }}
                    />
                  </div>
                </div>
              );
            })}
            <p className={styles.railNote}>
              Items waiting on a client are not yours to move today. Counting them against you is
              what made the old queue unreadable.
            </p>
          </RailPanel>

          <MoversPanel onOpenPractice={openPractice} />

          <RailPanel title="Closed this week">
            {closed.length === 0 && (
              <p className={styles.railNote}>Nothing closed yet this week.</p>
            )}
            {closed.map((item) => (
              <div key={item.id} className={styles.closedRow}>
                <span className={styles.closedAge}>{formatAge(item.resolvedAt)}</span>
                <span className={styles.closedTitle}>
                  {item.title}
                  <span className={styles.closedPractice}> · {item.practiceName}</span>
                </span>
              </div>
            ))}
          </RailPanel>
        </aside>
      </div>
    </div>
  );
}

interface Bucket {
  key: string;
  label: string;
  hint: string | null;
  items: WorkItem[];
}

/**
 * Labelled by what they sort on — due time — rather than by urgency words this screen deliberately
 * avoids (see `formatDue` in `components/portfolio/constants.ts`: no SLA was ever agreed, so nothing
 * here says "overdue"). Items with no due time get their own bucket instead of hiding inside "later".
 */
function groupByDue(items: WorkItem[], lane: BallWith): Bucket[] {
  if (lane === 'auto') {
    return [
      {
        key: 'auto',
        label: 'Handled automatically',
        hint: 'No action needed — here so nothing surprises you',
        items,
      },
    ];
  }
  return [
    {
      key: 'overdue',
      label: 'Past due time',
      hint: 'Waiting longest',
      items: items.filter((i) => i.hoursUntilDue !== null && i.hoursUntilDue < 0),
    },
    {
      key: 'today',
      label: 'Due soon',
      hint: 'Due within 12 hours',
      items: items.filter(
        (i) => i.hoursUntilDue !== null && i.hoursUntilDue >= 0 && i.hoursUntilDue <= 12,
      ),
    },
    {
      key: 'later',
      label: 'Due later',
      hint: null,
      items: items.filter((i) => i.hoursUntilDue !== null && i.hoursUntilDue > 12),
    },
    {
      key: 'undated',
      label: 'No due time',
      hint: 'No SLA — still real work',
      items: items.filter((i) => i.hoursUntilDue === null),
    },
  ];
}

function WeekStat({
  label,
  value,
  tone,
  sub,
}: {
  label: string;
  value: number | string;
  tone: 'error' | 'warning' | 'success' | 'neutral';
  sub: string;
}) {
  return (
    <div className={clsx(styles.stat, styles[`stat${tone}`])}>
      <span className={styles.statLabel}>{label}</span>
      <span className={styles.statValue}>{value}</span>
      <span className={styles.statSub}>{sub}</span>
    </div>
  );
}

function RailPanel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className={styles.railPanel}>
      <h2 className={styles.railTitle}>{title}</h2>
      {children}
    </section>
  );
}

function MoversPanel({ onOpenPractice }: { onOpenPractice: (practiceId: string) => void }) {
  const { data: movers, isLoading } = useMovers();

  return (
    <RailPanel title="Movers this week">
      {isLoading && <Skeleton height={60} />}
      {!isLoading && (movers?.length ?? 0) === 0 && (
        <p className={styles.railNote}>
          No practice has moved more than a couple of points. Health history builds up daily.
        </p>
      )}
      {movers?.map((mover) => (
        <button
          key={mover.practiceId}
          type="button"
          className={styles.moverRow}
          onClick={() => onOpenPractice(mover.practiceId)}
        >
          <span className={styles.moverName}>{mover.name}</span>
          <span className={clsx(styles.moverDelta, mover.delta < 0 ? styles.down : styles.up)}>
            {mover.delta > 0 ? '+' : ''}
            {mover.delta}
          </span>
        </button>
      ))}
    </RailPanel>
  );
}
